import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCompanyById, updateCompany } from '../api/company';
import { useAuth } from '../hooks/useAuth';
import { Company } from '../types/Company';
import Loader from '../components/Loader';
import ErrorPage from './ErrorPage';

const MyCompany: React.FC = () => {
    const queryClient = useQueryClient();
    const { state: authState } = useAuth();
    const companyId = authState.user?.company_id;

    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [editCompanyName, setEditCompanyName] = useState('');

    const { data: company, isLoading, isError, error } = useQuery<Company>({ 
        queryKey: ['company', companyId], 
        queryFn: () => getCompanyById(companyId!), // The ! asserts that companyId is not undefined
        enabled: !!companyId, // Only run the query if the companyId exists
    });

    const updateCompanyMutation = useMutation({ 
        mutationFn: updateCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company', companyId] });
            setEditModalOpen(false);
        }
    });

    useEffect(() => {
        if (company) {
            setEditCompanyName(company.name);
        }
    }, [company]);

    const handleUpdateCompany = (e: React.FormEvent) => {
        e.preventDefault();
        if (!company) return;
        updateCompanyMutation.mutate({ id: company.id, data: { name: editCompanyName } });
    };

    if (!companyId) {
        return <ErrorPage title="No Company Assigned" message="Your user account is not assigned to a company." />;
    }

    if (isLoading) {
        return <Loader />;
    }

    if (isError) {
        return <ErrorPage title="Error" message={`Failed to load company details: ${error?.message}`} />;
    }

    return (
        <div className="p-6">
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-4">
                        {company?.logo && <img src={company.logo} alt={company.name} className="w-16 h-16 rounded-full object-cover" />}
                        <div>
                            <h1 className="text-3xl font-bold">{company?.name}</h1>
                            <p className="text-gray-500">{company?.root_domain}</p>
                        </div>
                    </div>
                    <button onClick={() => setEditModalOpen(true)} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">Edit Company</button>
                </div>
            </div>

            {/* Edit Company Modal */}
            {isEditModalOpen && company && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Edit Company Details</h2>
                        <form onSubmit={handleUpdateCompany}>
                            <label htmlFor="edit-company-name" className="block text-sm font-medium text-gray-700">Company Name</label>
                            <input 
                                type="text"
                                id="edit-company-name"
                                value={editCompanyName}
                                onChange={(e) => setEditCompanyName(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                required
                            />
                            <div className="flex justify-end space-x-2 mt-6">
                                <button type="button" onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md" disabled={updateCompanyMutation.isPending}>
                                    {updateCompanyMutation.isPending ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyCompany;
